import { CATEGORIES, type Dimension, dec, toCanonical, ZERO } from "@bl/domain";
import { useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeft, Check } from "lucide-react";
import { useEffect, useState } from "react";
import { type Item, newKey, send, useItems, useLocations } from "../api";
import {
  Empty,
  ErrorNote,
  QuantityInput,
  type QuantityUnit,
  Toolbar,
  usePageTitle,
} from "../components/ui";
import {
  type Counted,
  farFromRecord,
  lastCounted,
  readCountDraft,
  rememberCounted,
  writeCountDraft,
} from "../countDraft";
import { categoryLabel, formLabel, showDateTime, showQuantity } from "../format";
import { useLayout } from "../layout/LayoutProvider";
import { useNotify } from "../notify";

type RowState = { saving: true } | { error: unknown } | { saved: string };

/** The sheet for one location: its rows, the keys of rows sent, and when it was begun and where from. */
interface Sheet {
  at: string;
  counts: Record<string, Counted>;
  keys: Record<string, string>;
  /** When the first row was typed, if any has been. */
  begun: string | null;
  /** The sheet was found on this device, left unrecorded, when the page opened. */
  kept: boolean;
}

/** What an item has on record at a location, canonical. */
const onRecordAt = (item: Item, locationId: string) =>
  item.onHand.byLocation.find((b) => b.locationId === locationId)?.quantity ?? "0";

/** A count as typed, in canonical units, or null while it is not a usable amount. Zero is a count. */
function canonicalOf(item: Item, c: Counted): string | null {
  if (c.value.trim() === "" || !/^\d*\.?\d*$/.test(c.value) || c.value === ".") return null;
  try {
    return toCanonical({ value: c.value, unit: c.unit }, item.dimension as Dimension, item.pack);
  } catch {
    return null;
  }
}

/**
 * A count sheet: everything on record in one location, counted in one go, the way a walk-in is walked
 * (the re-critique, 2026-10-06: Count took one item per dialog). Each row typed in is recorded as its
 * own count, against what the sheet showed when the row was begun, with both kept, so stock used or
 * received elsewhere meanwhile is not lost (docs/architecture.md, "Physical counts"). Rows left empty
 * are not counted. Each count is sent with its own idempotency key, kept until it is recorded, so a
 * retry after a dropped connection never counts twice.
 */
export function CountSheetPage() {
  usePageTitle("Count sheet", "Inventory");
  const { location: asked } = useSearch({ from: "/count" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const notify = useNotify();
  const { layout } = useLayout();
  const { data: items = [], isLoading, error } = useItems();
  const { data: locations = [] } = useLocations();
  const live = locations.filter((l) => !l.archived);
  // Where asked, else where this device last counted, else wherever holds the most.
  const remembered = lastCounted();
  const held = (id: string) =>
    items.filter((i) => !i.archived && !dec(onRecordAt(i, id)).eq(ZERO)).length;
  const fullest = [...live].sort((a, b) => held(b.id) - held(a.id))[0]?.id ?? "";
  const locationId =
    asked && live.some((l) => l.id === asked)
      ? asked
      : remembered && live.some((l) => l.id === remembered)
        ? remembered
        : fullest;
  const place = live.find((l) => l.id === locationId);
  const [everything, setEverything] = useState(false);
  const [sheet, setSheet] = useState<Sheet>({
    at: "",
    counts: {},
    keys: {},
    begun: null,
    kept: false,
  });
  const [states, setStates] = useState<Record<string, RowState>>({});
  const [saving, setSaving] = useState(false);

  // Each location's sheet is its own, picked up where this device left it.
  if (locationId && sheet.at !== locationId) {
    const kept = readCountDraft(locationId);
    setSheet({
      at: locationId,
      counts: kept?.value.counts ?? {},
      keys: kept?.value.keys ?? {},
      begun: kept?.savedAt ?? null,
      kept: kept !== null,
    });
    setStates({});
  }
  const counts = sheet.at === locationId ? sheet.counts : {};

  // Every change is kept on this device as it is typed, as brew day keeps its own.
  useEffect(() => {
    if (sheet.at) {
      writeCountDraft(
        sheet.at,
        { counts: sheet.counts, keys: sheet.keys },
        sheet.begun ?? new Date().toISOString(),
      );
    }
  }, [sheet]);

  // A row already typed stays on the sheet, whatever is on record: nothing typed is hidden.
  const here = items.filter(
    (i) =>
      !i.archived &&
      (everything || counts[i.id] !== undefined || !dec(onRecordAt(i, locationId)).eq(ZERO)),
  );
  const order = (c: string) => CATEGORIES.indexOf(c as (typeof CATEGORIES)[number]);
  const groups = [...new Set(here.map((i) => i.category))]
    .sort((a, b) => order(a) - order(b))
    .map((category) => ({
      category,
      items: here
        .filter((i) => i.category === category)
        .sort((a, b) => a.displayName.localeCompare(b.displayName)),
    }));
  const ready = here.filter((i) => {
    const c = counts[i.id];
    return c !== undefined && canonicalOf(i, c) !== null;
  });
  const far = ready.filter((i) => {
    const c = counts[i.id] as Counted;
    return farFromRecord(dec(c.expected), dec(canonicalOf(i, c) ?? "0")) !== null;
  });

  // Counted in bags where it comes in them (manifesto: "Counting stock is done in bags"), unless
  // this device shows weights.
  const unitFor = (i: Item): QuantityUnit =>
    i.pack && layout.inventory.quantityMode === "pack" ? "pack" : i.displayUnit;
  const type = (i: Item, value: string, unit: QuantityUnit) =>
    setSheet((s) => ({
      ...s,
      counts: {
        ...s.counts,
        [i.id]: { value, unit, expected: s.counts[i.id]?.expected ?? onRecordAt(i, s.at) },
      },
      begun: s.begun ?? new Date().toISOString(),
    }));
  const moveTo = (id: string) => {
    rememberCounted(id);
    navigate({ to: "/count", search: { location: id } });
  };
  const discard = () => {
    setSheet((s) => ({ ...s, counts: {}, begun: null, kept: false }));
    setStates({});
  };

  async function save() {
    setSaving(true);
    rememberCounted(locationId);
    let recorded = 0;
    for (const i of ready) {
      const c = counts[i.id] as Counted;
      const key = sheet.keys[i.id] ?? newKey();
      setSheet((s) => ({ ...s, keys: { ...s.keys, [i.id]: key } }));
      setStates((s) => ({ ...s, [i.id]: { saving: true } }));
      try {
        await send.movement(
          {
            kind: "adjust",
            itemId: i.id,
            locationId,
            counted: { value: c.value, unit: c.unit },
            expected: c.expected,
            note: "Count sheet",
          },
          key,
        );
        recorded++;
        setStates((s) => ({
          ...s,
          [i.id]: { saved: showQuantity(i, canonicalOf(i, c) ?? "0", "unit") },
        }));
        setSheet((s) => {
          const { [i.id]: _, ...counts } = s.counts;
          const { [i.id]: __, ...keys } = s.keys;
          const empty = Object.keys(counts).length === 0;
          return { ...s, counts, keys, begun: empty ? null : s.begun, kept: !empty && s.kept };
        });
      } catch (e) {
        setStates((s) => ({ ...s, [i.id]: { error: e } }));
      }
    }
    setSaving(false);
    await queryClient.invalidateQueries();
    if (recorded) {
      notify({
        title: `${recorded} ${recorded === 1 ? "count" : "counts"} recorded in ${place?.name ?? "this location"}`,
        link: { kind: "history" },
      });
    }
  }

  return (
    <>
      <Toolbar
        left={
          <Link to="/" className="toolbar-button" title="Back to inventory">
            <ArrowLeft size={20} />
            <span className="label">Inventory</span>
          </Link>
        }
      />
      <div className="page-body centered count-page">
        <h1 className="page-title">Count sheet</h1>
        <p className="muted count-intro">
          Type what you count; leave the rest. Each is recorded against what is on record here now,
          so stock used or received meanwhile still adds up. What you type is kept on this device
          until it is recorded.
        </p>
        <div className="count-controls">
          <label className="field">
            <span>Location</span>
            <select
              className="select"
              value={locationId}
              disabled={saving}
              onChange={(e) => moveTo(e.target.value)}
            >
              {live.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
          <label className="check-row count-everything">
            <input
              type="checkbox"
              checked={everything}
              onChange={(e) => setEverything(e.target.checked)}
            />
            Every item, not only what is on record here
          </label>
        </div>
        {sheet.kept && sheet.at === locationId && ready.length && sheet.begun ? (
          <div className="notice count-kept" role="status">
            <span>
              {ready.length} {ready.length === 1 ? "count" : "counts"} from{" "}
              {showDateTime(sheet.begun)}, kept on this device and not recorded yet.
            </span>
            <button type="button" className="link-button" disabled={saving} onClick={discard}>
              Discard {ready.length === 1 ? "it" : "them"}
            </button>
          </div>
        ) : null}
        <ErrorNote error={error} />
        {isLoading ? <p className="muted">Loading…</p> : null}
        {!isLoading && here.length === 0 ? (
          <Empty title={`Nothing is on record in ${place?.name ?? "this location"}`}>
            <button type="button" className="button" onClick={() => setEverything(true)}>
              Count every item here
            </button>
          </Empty>
        ) : null}
        {groups.map((g) => (
          <section key={g.category} className="count-group">
            <h2 className="brew-heading">{categoryLabel(g.category)}</h2>
            <ul className="count-list">
              {g.items.map((i) => {
                const c = counts[i.id];
                const record = c?.expected ?? onRecordAt(i, locationId);
                const canonical = c ? canonicalOf(i, c) : null;
                const delta = canonical === null ? null : dec(canonical).minus(dec(record));
                const check =
                  canonical === null ? null : farFromRecord(dec(record), dec(canonical));
                const state = states[i.id];
                const form = formLabel(i);
                return (
                  <li key={i.id} className="count-row">
                    <div className="count-what">
                      <strong>{i.displayName}</strong>
                      {form ? <span className="muted"> · {form}</span> : null}
                      <div className="muted num">On record {showQuantity(i, record, "unit")}</div>
                    </div>
                    <div className="count-amount">
                      <label htmlFor={`count-${i.id}`} className="sr-only">
                        Counted {i.displayName}
                      </label>
                      <QuantityInput
                        id={`count-${i.id}`}
                        value={c?.value ?? ""}
                        unit={c?.unit ?? unitFor(i)}
                        dimension={i.dimension as Dimension}
                        packName={i.pack?.name ?? null}
                        unitName={`Unit for ${i.displayName}`}
                        onChange={(value, unit) => type(i, value, unit)}
                      />
                      <div
                        className={`count-note num${check && !(state && "saved" in state) ? " far" : ""}`}
                        aria-live="polite"
                      >
                        {state && "saved" in state ? (
                          <span className="count-saved">
                            <Check size={14} aria-hidden /> Counted {state.saved}
                          </span>
                        ) : delta === null ? null : delta.eq(ZERO) ? (
                          "Matches the record"
                        ) : (
                          `${delta.gt(ZERO) ? "+" : "−"}${showQuantity(i, delta.abs().toString(), "unit")} from the record${check ? `: ${check}. Worth a second look` : ""}`
                        )}
                      </div>
                      {state && "error" in state ? <ErrorNote error={state.error} /> : null}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
      <div className="brew-actions">
        <span className="brew-save">
          {ready.length
            ? `${ready.length} ${ready.length === 1 ? "item" : "items"} counted${far.length ? `, ${far.length} far from the record` : ""}`
            : "Nothing counted yet"}
        </span>
        <button
          type="button"
          className="button primary"
          disabled={saving || ready.length === 0}
          onClick={save}
        >
          <Check size={18} /> {saving ? "Recording…" : "Record counts"}
        </button>
      </div>
    </>
  );
}
